/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts/SiteCharts';
import { endcUeSessionDropNrCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the EndcUeSessionDropNrCell component
 */
interface EndcUeSessionDropNrCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Name of the NR cell */
  cellName: string;
}

/**
 * ENDC UE Session Drop Rate Chart (NR Cell Level)
 * 
 * Displays EN-DC User Equipment session drop metrics for individual NR cells:
 * - Number of ENDC UE Session Drops (count)
 * - ENDC UE Session Drop Rate (percentage)
 * 
 * @component
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (endcUeSessionDropNrCellChartConfig)
 * @see API: /api/nr/cell/endc-ue-session-drop?cell_name={cellName}
 */

/**
 * ENDC UE Session Drop Rate Chart for NR Cell Level
 * 
 * Displays ENDC UE session drop count and drop rate for a specific NR cell.
 * Reuses the SiteCharts template component with NR cell-specific configuration.
 * 
 * Features:
 * - Orange bars: ENDC UE session drop count
 * - Blue line: Drop rate percentage
 * - Cell-specific filtering via additionalParams
 * - Time range controls (lastWeek, lastMonth, custom)
 * - Data aggregation (minute, hour, day)
 * - Export to CSV/PNG
 * - Fullscreen mode
 */
const EndcUeSessionDropNrCell: React.FC<EndcUeSessionDropNrCellProps> = ({
  onFullscreenToggle,
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts
      config={endcUeSessionDropNrCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default EndcUeSessionDropNrCell;
